import {useRef} from "react";
import type {ListModuleConfig, ListJourneyColumnIds} from "~/domainModels/listView/listModuleConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {ListCardStatus} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    navigateToEmotoradScheduleCallRoute,
    ottopilotDialPhoneNumber,
    resolveListCardFooterWhenEnabled,
    type EmotoradScheduleNavigateFn,
} from "~/components/ottopilotDataViewer/listCardPresentation";
import type {
    OttopilotListCardDisplayExtras,
    OttopilotListCardFooterBindings,
} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import type {RowEngagementHandlers} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementHandlers";
import type {ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";
import type {EventContainerScheduleCallDisableConfig} from "~/domainModels/workspaceModules/eventContainersModule";
import {getScheduleCallDisabledReason} from "~/domainModels/workspaceModules/eventContainerScheduleCallDisable";
import {
    useOttopilotListCardPresentation,
    type OttopilotListCardFunnelOptions,
} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import {useWorkspaceEventContainerTemplates} from "~/contexts/workspaceContext";
import {
    getEventContainerDispositionId,
    requireEventContainerTemplate,
} from "~/utilities/ottopilotSemanticColumns";

function stringifyAdditionalValue(raw: unknown): string {
    if (raw === undefined || raw === null) {
        return "";
    }
    if (typeof raw === "string") {
        return raw;
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    return "";
}

function omitContactFromMergedValues(merged: Record<Uuid, unknown>, contactColumnId: Uuid): Record<Uuid, unknown> {
    const next = {...merged};
    next[contactColumnId] = "";
    return next;
}

export type AssignedOuListCardFields = {
    getAssignedOuColumnLabel: (eventContainer: OttopilotEventContainer) => string;
    getAssignedOuName: (eventContainer: OttopilotEventContainer) => string | null;
};

export type ConfiguredJourneyListCardDeps = {
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    columnIds: ListJourneyColumnIds;
    journeysDisplayNameOttopilotStringTemplate: string;
    listConfig: ListModuleConfig;
    inQueueListConfig?: ListModuleConfig;
    mode: "leads" | "inQueue";
    resolveJourneyStatus: (journey: EventContainerWithEvents) => ListCardStatus;
    onLeadsDisposeRequest: (journey: EventContainerWithEvents) => void;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    notesEventContainerSourceLabel?: string;
    assignedOuListCardFields?: AssignedOuListCardFields;
    rowEngagement?: RowEngagementHandlers<EventContainerWithEvents>;
    sharedFooterBindings?: OttopilotListCardFooterBindings;
    funnelOptions?: OttopilotListCardFunnelOptions<EventContainerWithEvents>;
    isListCardFooterRestricted?: (journey: EventContainerWithEvents) => boolean;
    scheduleCallDisableConfig?: EventContainerScheduleCallDisableConfig;
    resolveListCardDisplayExtras?: (journey: EventContainerWithEvents) => OttopilotListCardDisplayExtras<EventContainerWithEvents>;
};

export function useConfiguredJourneyListCardPresentation(deps: ConfiguredJourneyListCardDeps) {
    const eventContainerTemplates = useWorkspaceEventContainerTemplates();
    const {navigate, columnIds} = deps;
    const modeRef = useRef(deps.mode);
    modeRef.current = deps.mode;
    const depsRef = useRef(deps);
    depsRef.current = deps;

    return useOttopilotListCardPresentation<EventContainerWithEvents>(
        {
            getDisplayName: (journey) =>
                processOttopilotStringTemplate(
                    depsRef.current.journeysDisplayNameOttopilotStringTemplate,
                    omitContactFromMergedValues(
                        journey.additionalColumnValues,
                        columnIds.contactNumber,
                    ),
                ),
            getPhoneNumber: (journey) => {
                const contactRaw = journey.additionalColumnValues[columnIds.contactNumber];
                return typeof contactRaw === "string" && contactRaw.trim() !== "" ? contactRaw : null;
            },
            getStatus: (journey) => depsRef.current.resolveJourneyStatus(journey),
            getCreatedAt: (journey) => journey.createdAt,
            getAssignedOuName: (journey) =>
                depsRef.current.assignedOuListCardFields?.getAssignedOuName(journey) ?? null,
            getAssignedOuColumnLabel: (journey) =>
                depsRef.current.assignedOuListCardFields?.getAssignedOuColumnLabel(journey) ?? "",
        },
        (journey, bindings) => {
            const d = depsRef.current;
            if (modeRef.current !== "leads") {
                return null;
            }
            return resolveListCardFooterWhenEnabled(d.listConfig.showFooter, bindings, journey.id);
        },
        {
            onPhoneClick: (journey, _bindings) => {
                const contactRaw = journey.additionalColumnValues[columnIds.contactNumber];
                ottopilotDialPhoneNumber(
                    typeof contactRaw === "string" && contactRaw.trim() !== "" ? contactRaw : null,
                );
            },
            onScheduleClick: (journey, _footerId, _bindings) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openSchedule(journey);
                    return;
                }
                const d = depsRef.current;
                const route =
                    modeRef.current === "inQueue" && d.inQueueListConfig !== undefined
                        ? d.inQueueListConfig.scheduleRoute
                        : d.listConfig.scheduleRoute;
                navigateToEmotoradScheduleCallRoute(navigate, route, d.workspaceId, journey.id);
            },
            onDisposeClick: (journey, _footerId, _bindings) => {
                if (modeRef.current !== "leads") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openDisposition(journey);
                    return;
                }
                depsRef.current.onLeadsDisposeRequest(journey);
            },
            onCreateClick: (journey, _footerId, bindings) => {
                if (modeRef.current !== "leads") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openCreate(journey);
                    return;
                }
                bindings.setSelectedCreateJourneyId(journey.id);
                bindings.setSelectedDispositoinId(
                    getEventContainerDispositionId(
                        journey,
                        requireEventContainerTemplate(eventContainerTemplates, journey.templateId),
                    ),
                );
            },
            onLogCallClick:
                deps.listConfig.mobileReplaceCreateWithLogCall === true
                    ? (journey, _footerId, _bindings) => {
                          if (modeRef.current !== "leads") {
                              return;
                          }
                          const engagement = depsRef.current.rowEngagement;
                          if (engagement !== undefined) {
                              engagement.openLogCall(journey);
                          }
                      }
                    : undefined,
            onNotesClick: (journey) => {
                if (modeRef.current !== "leads") {
                    return;
                }
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openNotes(journey);
                    return;
                }
                depsRef.current.openNotesPanel({
                    eventContainerId: journey.id,
                    eventContainer: journey,
                    createScope: {scopeType: "eventContainer"},
                    eventContainerSourceLabel: depsRef.current.notesEventContainerSourceLabel ?? "OttopilotEventContainer",
                });
            },
            isListCardFooterRestricted: (journey) =>
                depsRef.current.isListCardFooterRestricted?.(journey) === true,
            isScheduleClickDisabled: (journey) =>
                getScheduleCallDisabledReason(journey, depsRef.current.scheduleCallDisableConfig) !== null,
            resolveListCardDisplayExtras: (journey) => depsRef.current.resolveListCardDisplayExtras?.(journey) ?? {},
        },
        deps.funnelOptions,
        deps.sharedFooterBindings,
    );
}

export type ConfiguredEventListCardDeps = {
    eventTemplate: OttopilotEventTemplate;
    workspaceId: Uuid;
    navigate: EmotoradScheduleNavigateFn;
    columnIds: ListJourneyColumnIds;
    listConfig: ListModuleConfig;
    resolveScheduleRoute?: (eventTemplateId: Uuid) => string;
    resolveEventStatus: (event: EventWithEventContainer) => ListCardStatus;
    onDisposeRequest: (event: EventWithEventContainer) => void;
    onOpenCreateDrawer: () => void;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    notesEventContainerSourceLabel?: string;
    /** Event Container display template when the calendar title template is absent or empty. */
    journeysDisplayNameOttopilotStringTemplate?: string;
    /** Registry workspaces (e.g. Dulux) resolve the lead/customer name from Event Container columns. */
    resolveEventListDisplayName?: (event: EventWithEventContainer) => string;
    rowEngagement?: RowEngagementHandlers<EventWithEventContainer>;
    sharedFooterBindings?: OttopilotListCardFooterBindings;
    assignedOuListCardFields?: AssignedOuListCardFields;
    funnelOptions?: OttopilotListCardFunnelOptions<EventWithEventContainer>;
    isListCardFooterRestricted?: (event: EventWithEventContainer) => boolean;
    scheduleCallDisableConfig?: EventContainerScheduleCallDisableConfig;
};

export function useConfiguredEventListCardPresentation(deps: ConfiguredEventListCardDeps) {
    const {eventTemplate, workspaceId, navigate, columnIds, listConfig} = deps;
    const depsRef = useRef(deps);
    depsRef.current = deps;

    return useOttopilotListCardPresentation<EventWithEventContainer>(
        {
            getDisplayName: (event) => {
                const titleTemplate = eventTemplate.configuration.eventPage.calendarConfig?.calendarTitleOttopilotStringTemplate;
                const merged = omitContactFromMergedValues(
                    {
                        ...event.eventContainer.additionalColumnValues,
                        ...event.additionalColumnValues,
                    },
                    columnIds.contactNumber,
                );
                if (titleTemplate !== undefined) {
                    const fromCalendar = processOttopilotStringTemplate(titleTemplate, merged);
                    if (fromCalendar.trim() !== "") {
                        return fromCalendar;
                    }
                }
                if (deps.resolveEventListDisplayName !== undefined) {
                    return deps.resolveEventListDisplayName(event);
                }
                if (deps.journeysDisplayNameOttopilotStringTemplate !== undefined) {
                    const fromJourney = processOttopilotStringTemplate(
                        deps.journeysDisplayNameOttopilotStringTemplate,
                        omitContactFromMergedValues(
                            event.eventContainer.additionalColumnValues,
                            columnIds.contactNumber,
                        ),
                    );
                    if (fromJourney.trim() !== "") {
                        return fromJourney;
                    }
                }
                return eventTemplate.configuration.displayNameVariants.singularSentenceCase;
            },
            getPhoneNumber: (event) => {
                const raw = event.eventContainer.additionalColumnValues[columnIds.contactNumber];
                const phone = stringifyAdditionalValue(raw).trim();
                return phone === "" ? null : phone;
            },
            getStatus: (event) => depsRef.current.resolveEventStatus(event),
            getCreatedAt: (event) => event.createdAt,
            getAssignedOuName: (event) =>
                depsRef.current.assignedOuListCardFields?.getAssignedOuName(event.eventContainer) ?? null,
            getAssignedOuColumnLabel: (event) =>
                depsRef.current.assignedOuListCardFields?.getAssignedOuColumnLabel(event.eventContainer) ?? "",
        },
        (event, bindings) => resolveListCardFooterWhenEnabled(listConfig.showFooter, bindings, event.id),
        {
            onPhoneClick: (event, _bindings) => {
                const raw = event.eventContainer.additionalColumnValues[columnIds.contactNumber];
                ottopilotDialPhoneNumber(stringifyAdditionalValue(raw).trim());
            },
            onScheduleClick: (event, _footerId, _bindings) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openSchedule(event);
                    return;
                }
                const scheduleRoute = deps.resolveScheduleRoute?.(eventTemplate.id) ?? listConfig.scheduleRoute;
                navigateToEmotoradScheduleCallRoute(
                    navigate,
                    scheduleRoute,
                    workspaceId,
                    event.eventContainer.id,
                );
            },
            onDisposeClick: (event, _footerId, _bindings) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openDisposition(event);
                    return;
                }
                deps.onDisposeRequest(event);
            },
            onCreateClick: (event, _footerId, bindings) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openCreate(event);
                    return;
                }
                bindings.setSelectedCreateJourneyId(event.eventContainer.id);
                deps.onOpenCreateDrawer();
            },
            onLogCallClick:
                listConfig.mobileReplaceCreateWithLogCall === true
                    ? (event, _footerId, _bindings) => {
                          const engagement = depsRef.current.rowEngagement;
                          if (engagement !== undefined) {
                              engagement.openLogCall(event);
                          }
                      }
                    : undefined,
            onNotesClick: (event) => {
                const engagement = depsRef.current.rowEngagement;
                if (engagement !== undefined) {
                    engagement.openNotes(event);
                    return;
                }
                deps.openNotesPanel({
                    eventContainerId: event.eventContainer.id,
                    eventContainer: event.eventContainer,
                    createScope: {
                        scopeType: "event",
                        eventId: event.id,
                        eventTemplateId: event.templateId,
                    },
                    eventContainerSourceLabel: deps.notesEventContainerSourceLabel ?? "OttopilotEventContainer",
                });
            },
            isListCardFooterRestricted: (event) =>
                depsRef.current.isListCardFooterRestricted?.(event) === true,
            isScheduleClickDisabled: (event) =>
                getScheduleCallDisabledReason(event.eventContainer, depsRef.current.scheduleCallDisableConfig) !== null,
        },
        deps.funnelOptions,
        deps.sharedFooterBindings,
    );
}
